import global from "../../../common/utility/global";
import { NOMBRES_MES_CORTO } from "./constants";

export function formatCurrency(value, moneda = "PEN") {
    const numero = Number(value) || 0;
    const simbolo = moneda === "USD" ? "US$" : "S/";
    return `${simbolo} ${global.decimalFormat(numero.toFixed(2))}`;
}

export function formatNumber(value, decimales = 1) {
    const numero = Number(value) || 0;
    return global.decimalFormat(numero.toFixed(decimales));
}

const MAX_CARACTERES_NOMBRE = 18;

// Recorta nombres largos para que la leyenda/tooltip mantengan siempre el
// mismo ancho/tamano; sin esto, un vendedor con nombre muy largo estira la
// leyenda y deforma el layout del grafico.
export function truncarNombre(nombre) {
    if (!nombre) return "";
    return nombre.length > MAX_CARACTERES_NOMBRE
        ? `${nombre.slice(0, MAX_CARACTERES_NOMBRE)}...`
        : nombre;
}

// "2026-08-27" -> Date local (evita el corrimiento de dia que da
// "new Date('2026-08-27')" al interpretarse como UTC en un huso horario negativo).
function parsearFechaLocal(fechaStr) {
    const [anio, mes, dia] = fechaStr.slice(0, 10).split("-").map(Number);
    return new Date(anio, mes - 1, dia);
}

function obtenerClaveTiempo(fecha, esMismoMes) {
    return esMismoMes
        ? `${fecha.getDate()} ${NOMBRES_MES_CORTO[fecha.getMonth()]}`
        : `${NOMBRES_MES_CORTO[fecha.getMonth()]} ${fecha.getFullYear()}`;
}

// Arma la serie de "Evolucion" (top 10 vendedores) a partir del detalle
// diario que devuelve el servicio: una fila por vendedor y fecha, ya
// separada en soles y dolares (totalSoles/totalDolares). Se agrupa por dia
// si el filtro cae en un mismo mes, o por mes si abarca mas de uno (mismo
// criterio que construirEvolucionDesdeDetalle de DashboardEstablecimiento).
function construirEvolucionDesdeDetalle(detalle, filter, vendedoresReferencia) {
    const esMismoMes = filter.anioInicio === filter.anioFin && filter.mesInicio === filter.mesFin;
    const nombresTop = vendedoresReferencia.slice(0, 10).map((item) => item.nombre);

    const detalleOrdenado = detalle
        .map((fila) => ({ ...fila, __fecha: parsearFechaLocal(fila.fecha) }))
        .sort((a, b) => a.__fecha - b.__fecha);

    const ordenClaves = [];
    const vistos = new Set();
    detalleOrdenado.forEach((fila) => {
        const clave = obtenerClaveTiempo(fila.__fecha, esMismoMes);
        if (!vistos.has(clave)) {
            vistos.add(clave);
            ordenClaves.push(clave);
        }
    });

    const acumuladoPorVendedor = {};
    nombresTop.forEach((nombre) => {
        acumuladoPorVendedor[nombre] = {};
    });
    detalleOrdenado.forEach((fila) => {
        if (!nombresTop.includes(fila.nombre)) return;
        const clave = obtenerClaveTiempo(fila.__fecha, esMismoMes);
        const previo = acumuladoPorVendedor[fila.nombre][clave] || { pen: 0, usd: 0 };
        previo.pen += Number(fila.totalSoles) || 0;
        previo.usd += Number(fila.totalDolares) || 0;
        acumuladoPorVendedor[fila.nombre][clave] = previo;
    });

    const construirSerie = (moneda) => nombresTop.map((nombre) => ({
        name: nombre,
        data: ordenClaves.map((clave) => Math.round((acumuladoPorVendedor[nombre][clave] || { pen: 0, usd: 0 })[moneda])),
    }));

    return {
        timeLine: ordenClaves,
        pen: construirSerie("pen"),
        usd: construirSerie("usd"),
    };
}

function direccionTendencia(actual, anterior) {
    if (actual > anterior) return "up";
    if (actual < anterior) return "down";
    return "flat";
}

// % de variacion actual vs anterior. Si el periodo anterior no tuvo
// movimiento, se evita la division entre 0: sin cambio real (0/0) queda
// plano, y una aparicion nueva (valor/0) se marca como +100%.
function calcularTendenciaPct(actual, anterior) {
    if (!anterior) return actual > 0 ? 100 : 0;
    return ((actual - anterior) / anterior) * 100;
}

function construirKpiMoneda(actual, anterior) {
    const valorActual = Number(actual) || 0;
    const valorAnterior = Number(anterior) || 0;
    return {
        valor: valorActual,
        tendenciaPct: calcularTendenciaPct(valorActual, valorAnterior),
        tendenciaDireccion: direccionTendencia(valorActual, valorAnterior),
    };
}

// Cpe Promedio y Ventas por Vendedor (Promedio) no llegan calculados del
// backend; se derivan del total vendido entre, respectivamente, la
// cantidad de comprobantes y la cantidad de vendedores activos.
function calcularPromedio(total, cantidad) {
    return cantidad > 0 ? Number(total) / Number(cantidad) : 0;
}

// Concentracion Top 3 se expresa en puntos porcentuales (p.p.), no en %
// relativo: la variacion es la resta directa entre el % actual y el % del
// periodo anterior, cada uno ya calculado sobre su propio total.
function construirConcentracionTop(ventasTop, ventasTopAnterior, totalVentas, totalVentasAnterior) {
    const pctActual = totalVentas > 0 ? (Number(ventasTop) / totalVentas) * 100 : 0;
    const pctAnterior = totalVentasAnterior > 0 ? (Number(ventasTopAnterior) / totalVentasAnterior) * 100 : 0;
    return {
        valor: pctActual,
        tendenciaPct: pctActual - pctAnterior,
        tendenciaDireccion: direccionTendencia(pctActual, pctAnterior),
    };
}

export function buildDashboardData(response, filter) {
    // El servicio real (comprobanteService.dashboardVendedor) devuelve:
    // - vendedores: detalle diario (nombre del vendedor, fecha,
    //   totalSoles, totalDolares), una fila por vendedor y fecha con ventas.
    // - kpis: arreglo de un solo elemento con los totales del periodo y su
    //   comparacion contra el periodo anterior (sufijo "Anterior"):
    //   totalVentasSoles/Dolares, cantidadCPE, vendedorActivos y
    //   ventasTop10Soles/Dolares (usado para la concentracion Top 3).
    const detalle = (response && response.vendedores) || [];
    const kpisApi = (response && response.kpis && response.kpis[0]) || {};

    const acumuladoPorVendedor = {};
    detalle.forEach((fila) => {
        const previo = acumuladoPorVendedor[fila.nombre] || { pen: 0, usd: 0 };
        previo.pen += Number(fila.totalSoles) || 0;
        previo.usd += Number(fila.totalDolares) || 0;
        acumuladoPorVendedor[fila.nombre] = previo;
    });

    const vendedoresOrdenados = Object.keys(acumuladoPorVendedor)
        .map((nombre) => ({ nombre, pen: acumuladoPorVendedor[nombre].pen, usd: acumuladoPorVendedor[nombre].usd }))
        .sort((a, b) => b.pen - a.pen);

    const totalVentasPen = vendedoresOrdenados.reduce((acumulado, item) => acumulado + item.pen, 0);
    const totalVentasUsd = vendedoresOrdenados.reduce((acumulado, item) => acumulado + item.usd, 0);

    const principales = vendedoresOrdenados.slice(0, 4);
    const otros = vendedoresOrdenados.slice(4);
    const totalOtrosPen = otros.reduce((acumulado, item) => acumulado + item.pen, 0);
    const totalOtrosUsd = otros.reduce((acumulado, item) => acumulado + item.usd, 0);
    const participacionBase = otros.length > 0
        ? [...principales, { nombre: `Otros (${otros.length} vendedores)`, pen: totalOtrosPen, usd: totalOtrosUsd }]
        : principales;

    const construirParticipacion = (moneda) => {
        const totalMoneda = moneda === "pen" ? totalVentasPen : totalVentasUsd;
        return participacionBase.map((item) => ({
            nombre: item.nombre,
            total: item[moneda],
            porcentaje: totalMoneda > 0 ? (item[moneda] / totalMoneda) * 100 : 0,
        }));
    };

    const evolucion = construirEvolucionDesdeDetalle(detalle, filter, vendedoresOrdenados);

    const cantidadCPE = Number(kpisApi.cantidadCPE) || 0;
    const cantidadCPEAnterior = Number(kpisApi.cantidadCPEAnterior) || 0;
    const vendedorActivos = Number(kpisApi.vendedorActivos) || 0;
    const vendedorActivosAnterior = Number(kpisApi.vendedorActivosAnterior) || 0;

    return {
        kpis: {
            totalVentas: {
                pen: construirKpiMoneda(kpisApi.totalVentasSoles, kpisApi.totalVentasSolesAnterior),
                usd: construirKpiMoneda(kpisApi.totalVentasDolares, kpisApi.totalVentasDolaresAnterior),
            },
            vendedoresActivos: {
                valor: vendedorActivos,
                tendenciaPct: calcularTendenciaPct(vendedorActivos, vendedorActivosAnterior),
                tendenciaDireccion: direccionTendencia(vendedorActivos, vendedorActivosAnterior),
            },
            // El backend no manda un Cpe Promedio ya calculado: se deriva del
            // total vendido entre la cantidad de comprobantes, en ambos
            // periodos, para poder sacar la tendencia.
            cpePromedio: {
                pen: construirKpiMoneda(
                    calcularPromedio(kpisApi.totalVentasSoles, cantidadCPE),
                    calcularPromedio(kpisApi.totalVentasSolesAnterior, cantidadCPEAnterior)
                ),
                usd: construirKpiMoneda(
                    calcularPromedio(kpisApi.totalVentasDolares, cantidadCPE),
                    calcularPromedio(kpisApi.totalVentasDolaresAnterior, cantidadCPEAnterior)
                ),
            },
            // Igual que Cpe Promedio: se deriva del total vendido entre la
            // cantidad de vendedores activos de cada periodo.
            ventasPromedioPorVendedor: {
                pen: construirKpiMoneda(
                    calcularPromedio(kpisApi.totalVentasSoles, vendedorActivos),
                    calcularPromedio(kpisApi.totalVentasSolesAnterior, vendedorActivosAnterior)
                ),
                usd: construirKpiMoneda(
                    calcularPromedio(kpisApi.totalVentasDolares, vendedorActivos),
                    calcularPromedio(kpisApi.totalVentasDolaresAnterior, vendedorActivosAnterior)
                ),
            },
            concentracionTop3: {
                pen: construirConcentracionTop(
                    kpisApi.ventasTop10Soles, kpisApi.ventasTop10SolesAnterior,
                    Number(kpisApi.totalVentasSoles) || 0, Number(kpisApi.totalVentasSolesAnterior) || 0
                ),
                usd: construirConcentracionTop(
                    kpisApi.ventasTop10Dolares, kpisApi.ventasTop10DolaresAnterior,
                    Number(kpisApi.totalVentasDolares) || 0, Number(kpisApi.totalVentasDolaresAnterior) || 0
                ),
            },
        },
        topVendedores: vendedoresOrdenados.slice(0, 10),
        participacion: {
            pen: construirParticipacion("pen"),
            usd: construirParticipacion("usd"),
        },
        evolucion,
    };
}
